import {
  Alert,
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import { useContext } from "react";
import { UserProgramContext } from "../context/user-program/user-program";

import { ContainedButton } from "./styled/contained-button";
import { OutlinedButton } from "./styled/outlined-button";
import {
  CLINIC_PREVIEW_ALERT_PARAGRAPHS,
  GREETING_VIDEO_YOUTUBE_ID,
} from "./constants";
import {
  SUBROUTE_HOME_SECTION_DOCUMENTS,
  SUBROUTE_HOME_SECTION_ACUTY_CALENDAR,
  SUBROUTE_HOME_SECTION_VIDEOS,
} from "../constants";
import YouTube from "react-youtube";
import { ClinicWeightPresentationText } from "./clinic/clinic-weight-presentation-text";
import { Navbar } from "./styled/nav-bar";

export const SectionsList = () => {
  const navigate = useNavigate();

  const navigateToSection = (sectionId: string) => {
    // Set default subroute based on section
    let defaultSubroute = SUBROUTE_HOME_SECTION_DOCUMENTS; // default

    if (sectionId === "Ernährung") {
      defaultSubroute = SUBROUTE_HOME_SECTION_ACUTY_CALENDAR; // monthly-training
    } else if (sectionId === "Verhaltenstraining") {
      defaultSubroute = SUBROUTE_HOME_SECTION_VIDEOS; // videos
    }

    navigate(`/sections/${sectionId}/${defaultSubroute}`);
  };

  const { program, role } = useContext(UserProgramContext);

  const isClinicWeight = role === "clinic-weight";

  if (!program) {
    return null;
  }

  return (
    <Grid
      container
      flexDirection="column"
      alignItems="center"
      spacing={4}
      pt={2}
      pr={2}
      pl={2}
      pb={5}
    >
      {["clinic-preview"].includes(role) && (
        <Grid item xs={12} md={10} sx={{ width: "100%" }}>
          <Alert severity="info">
            {CLINIC_PREVIEW_ALERT_PARAGRAPHS.map((paragraph) => (
              <Typography variant="body2" mb={2}>
                {paragraph}
              </Typography>
            ))}
          </Alert>
        </Grid>
      )}

      <Navbar />

      {/* info */}
      <Grid item xs={12} md={10} sx={{ width: "100%" }}>
        <Card sx={{ borderRadius: "25px" }}>
          <CardContent>
            <Grid
              flexDirection={{ xs: "column-reverse", md: "row" }}
              container
              alignItems="center"
              justifyContent="space-between"
              flexWrap="nowrap"
              spacing={2}
            >
              {isClinicWeight ? (
                <ClinicWeightPresentationText />
              ) : (
                <>
                  <Grid item>
                    <Typography variant="h6" ml={{ xs: 0, md: 3 }} mb={2}>
                      Liebe Patientinnen und Patienten,
                    </Typography>
                    <Typography variant="body2" ml={{ xs: 0, md: 3 }} mb={2}>
                      Wir freuen uns sehr, Sie bei uns begrüßen zu dürfen. Um
                      Ihren Start zu erleichtern, folgen Sie bitte diesen
                      Schritten
                    </Typography>
                    <Typography variant="body2" ml={{ xs: 0, md: 3 }} mb={2}>
                      1.{" "}
                      <Typography component="span" fontWeight="bold">
                        Begrüßungsvideo anschauen:
                      </Typography>{" "}
                      Starten Sie mit unserem kurzen Einführungsvideo auf dieser
                      Seite, um einen Überblick über alle verfügbaren Funktionen
                      und Services zu erhalten. Klicken Sie auf das Video.
                    </Typography>
                    <Typography variant="body2" ml={{ xs: 0, md: 3 }} mb={2}>
                      2.{" "}
                      <Typography component="span" fontWeight="bold">
                        Anleitung lesen:
                      </Typography>{" "}
                      Bitte nehmen Sie sich einen Moment Zeit und lesen Sie die
                      Anleitung. Klicken Sie dafür auf die Schaltfläche
                      „Anleitung lesen“.
                    </Typography>
                    <Typography variant="body2" ml={{ xs: 0, md: 3 }} mb={2}>
                      3.{" "}
                      <Typography component="span" fontWeight="bold">
                        MMK-Infoveranstaltung buchen:
                      </Typography>{" "}
                      Klicken Sie anschließend auf die Schaltfläche
                      “MMK-Infoveranstaltung” und wählen Sie einen Termin aus.
                      Die Teilnahme an der MMK-Infoveranstaltung ist eine
                      einmalige Pflichtveranstaltung. Hier bekommen Sie
                      sämtliche Informationen rund um unsere MMK-Seite
                      vermittelt, was wir von Ihnen benötigen, auf was Sie alles
                      achten müssen und Sie können Fragen stellen. Wir freuen
                      uns sehr auf das Gespräch mit Ihnen.{" "}
                    </Typography>
                    <Typography variant="body2" ml={{ xs: 0, md: 3 }} mb={2}>
                      4.{" "}
                      <Typography component="span" fontWeight="bold">
                        Beginn der Ernährungstherapie:
                      </Typography>{" "}
                      Der Zeitraum für ihre Ernährungstherapie beginnt{" "}
                      <Typography component="span" fontWeight="bold">
                        mit ihrer ersten Teilnahme an der monatlichen Schulung.
                      </Typography>{" "}
                      Abhängig von der Dauer ihres MMK-Programms (z.B 3 order 6
                      Monate) startet dieser Zeitraum entsprechend. Bitte
                      beachten Sie dies bei der Plannung ihrer Therapie.{" "}
                      <Typography component="span" fontWeight="bold">
                        Sie können bereits jetzt den ersten Termin Ihrer
                        Ernährungsschulung buchen:
                      </Typography>{" "}
                      Klicken Sie bei „Ernährung“ auf „Module ansehen“.
                      Anschließend klicken Sie auf „Termin für monatliche
                      Schulung buchen“.
                    </Typography>{" "}
                    <Typography variant="body2" ml={{ xs: 0, md: 3 }} mb={2}>
                      Bei Fragen stehen wir Ihnen jederzeit zur Verfügung.
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Link
                        style={{ marginLeft: "xs: 0, md: 3" }}
                        target="_blank"
                        to="https://www.adipositasgutachten.de/anleitung-mmk-programm"
                      >
                        <ContainedButton>Anleitung lesen</ContainedButton>
                      </Link>
                      {
                        /* Only show MMK appointment link if the user is not in clinic preview role */
                        role !== "clinic-preview" && (
                          <Link
                            style={{ marginLeft: "xs: 0, md: 3" }}
                            target="_blank"
                            to="https://adipositasgutachten.as.me/?appointmentType=54238442"
                          >
                            <ContainedButton>
                              MMK-Infoveranstaltung buchen
                            </ContainedButton>
                          </Link>
                        )
                      }
                      {role === "clinic-preview" && (
                        <Box style={{ marginLeft: "xs: 0, md: 3" }}>
                          <ContainedButton>
                            MMK-Infoveranstaltung buchen
                          </ContainedButton>
                        </Box>
                      )}
                    </Stack>
                  </Grid>
                  <Grid item minWidth="400px" maxWidth="500px">
                    <YouTube
                      videoId={GREETING_VIDEO_YOUTUBE_ID}
                      id={GREETING_VIDEO_YOUTUBE_ID}
                      style={{ width: "100%", aspectRatio: "16/9" }}
                      opts={{ width: "100%", height: "100%" }}
                      title="Begrüßungsvideo"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* section cards */}
      <Grid
        container
        justifyContent="space-around"
        item
        spacing={3}
        xs={12}
        md={10}
      >
        {program.sections.map((section) => (
          <Grid key={section.title} item xs={12} md={6} lg={4}>
            <Card
              sx={{ borderRadius: "20px", maxWidth: "400px", margin: "auto" }}
              key={section.title}
            >
              <CardMedia
                sx={{ objectFit: "cover", maxHeight: "300px" }}
                component="img"
                image={section.coverUrl}
                alt={`Section ${section.title}}`}
              />
              <CardContent
                sx={{
                  backgroundColor: "#f5f5f5",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  boxSizing: "border-box",
                }}
              >
                <Typography
                  align="center"
                  gutterBottom
                  variant="h5"
                  component="div"
                >
                  {section.title === "Verhaltenstraining"
                    ? "Verhalten"
                    : section.title}
                </Typography>
                <OutlinedButton
                  size="large"
                  onClick={() => navigateToSection(section.title)}
                >
                  ansehen
                </OutlinedButton>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* contact form button */}
      <Grid item xs={12}>
        <Link target="_blank" to="https://www.adipositasgutachten.de/kontakt">
          <ContainedButton>
            Haben Sie Fragen oder Anmerkungen? Wir sind für Sie da.
          </ContainedButton>
        </Link>
      </Grid>
    </Grid>
  );
};
